import { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Alert, Button, Form, Spinner } from 'react-bootstrap';
import {
  FiArrowLeft as ArrowLeft,
  FiCheck as Check,
  FiCheckCircle as CheckCircle2,
  FiFileText as FileText,
  FiInfo as Info,
  FiPackage as PackageOpen,
  FiUploadCloud as UploadCloud,
  FiXCircle as XCircle2,
} from 'react-icons/fi';
import { TbSparkles as Sparkles, TbWallet as Wallet } from 'react-icons/tb';
import {
  trackerFrequencies,
  trackerResultDepths,
} from '../pricing';
import walletApi, {
  useGetWalletBalanceQuery,
} from '@/app/Wallet/api/walletApi';
import useDomain from '@/hooks/useDomain';
import { useValidateIntroCodeMutation } from '@/app/Purchase/api/ordersApi';
import {
  calcLineTax,
  getAffiliateCookieCode,
  getIntroCodeDeductionLabel,
  getIntroCodeDiscountAmount,
  getIntroCodeDiscountBreakdown,
  getIntroCodeErrorFromCatch,
  getIntroCodeErrorMessage,
  getIntroCodeWalletCreditLabel,
  isApplicableAffiliateIntroCodeResponse,
  isValidIntroCodeResponse,
} from '@/app/Purchase/utils/introCode';
import { useGetTooltipsQuery } from '@/app/common/api/tooltipsApi';
import HelpTooltip from '@/app/common/components/HelpTooltip';
import { isUsableWalletBalance } from '@/app/Wallet/utils/walletBalance';
import {
  numberFormat,
  settings,
  sortedProjectTools,
  ui,
} from '../constants';
import { usePricingTariffs } from '../context/PricingTariffsContext';
import pricingApi, { useCreateOrderMutation } from '../api/pricingApi';
import { ANNUAL_PLAN_BLOCKS_MONTHLY_CODE } from '../types/pricingApiTypes';
import {
  fillTemplate,
  formatToman,
  consumeCheckoutSuccessReload,
  reloadAfterCheckoutSuccess,
  uploadOfflineReceipt,
} from '../utils';
import { isProjectToolAccessible, isPaidPlan } from '../utils/planToolAccess';
import {
  getNoPlanTariffPriceId,
  getPlanPrice,
  formatPlanInvoiceItemLabel,
  formatPlanPeriodSummaryLabel,
  formatPlanSubscriptionLabel,
  isFreePlan,
} from '../utils/tariffPricing';
import {
  buildCheckoutPayload,
  getCreateOrderErrorMessage,
  toCreateOrderRequest,
} from '../utils/buildCheckoutPayload';
import { getPreviousPlanBalance } from '../utils/previousPlanBalance';
import CheckoutFeedbackModal from './CheckoutFeedbackModal';
import InvoiceModal from './InvoiceModal';
import LegalModal from '@/app/Purchase/components/LegalModal';
import { ROUTES } from '@/app/Purchase/constants';
import type {
  AppliedIntroCode,
  InvoiceData,
  MentoringPlanKey,
  OfflineReceiptStatus,
  PeriodDays,
  PlanId,
  TrackerFrequencyId,
  TrackerResultDepth,
} from '../types';

interface SummaryProps {
  selectedPlan: PlanId | null;
  period: PeriodDays;
  trackerEnabled: boolean;
  keywords: number;
  frequency: TrackerFrequencyId;
  resultDepth: TrackerResultDepth;
  mentoringEnabled: boolean;
  mentoringPlanKey: MentoringPlanKey;
  walletTopUpEnabled: boolean;
  walletTopUpAmount: number;
  isMemberStep?: boolean;
}

function Summary({
  selectedPlan,
  period,
  trackerEnabled,
  keywords,
  frequency,
  resultDepth,
  mentoringEnabled,
  mentoringPlanKey,
  walletTopUpEnabled,
  walletTopUpAmount,
  isMemberStep = false,
}: SummaryProps) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const {
    plans,
    tariffs,
    mentoringPlans,
    trackerDailyPrice,
    trackerAddonId,
    walletAddonId,
    realAndLegal,
    remainingAmount,
    isLoading: isLoadingTariffs,
  } = usePricingTariffs();
  const { domain_slug } = useDomain();
  const { data: walletBalanceResponse } = useGetWalletBalanceQuery(
    { domain_slug: domain_slug ?? '' },
    {
      skip: isMemberStep || !domain_slug,
    }
  );
  const { data: tooltips = [] } = useGetTooltipsQuery();
  const tooltipBySlug = useMemo(
    () =>
      Object.fromEntries(
        tooltips.flatMap((item) => {
          const description = item.description?.trim();
          return description ? [[item.slug, description]] : [];
        }),
      ),
    [tooltips],
  );
  const walletBalance =
    walletBalanceResponse?.personalBalance ??
    walletBalanceResponse?.balance ??
    0;
  const previousPlanBalance = selectedPlan
    ? getPreviousPlanBalance(remainingAmount)
    : 0;
  const [createOrder, { isLoading: isCreatingOrder }] = useCreateOrderMutation();
  const [validateIntroCode] = useValidateIntroCodeMutation();
  const [isApplyingManualIntroCode, setIsApplyingManualIntroCode] = useState(false);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [checkoutErrorMessage, setCheckoutErrorMessage] = useState<string | null>(
    null,
  );
  const [checkoutErrorVariant, setCheckoutErrorVariant] = useState<
    'danger' | 'warning'
  >('danger');
  const [checkoutSuccessOpen, setCheckoutSuccessOpen] = useState(false);
  const [useWallet, setUseWallet] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [invoiceOpen, setInvoiceOpen] = useState(false);
  const [legalModalOpen, setLegalModalOpen] = useState(false);
  const [legalSuccessOpen, setLegalSuccessOpen] = useState(false);
  const [offlinePaymentEnabled, setOfflinePaymentEnabled] = useState(false);
  const [offlineReceiptFile, setOfflineReceiptFile] = useState<File | null>(null);
  const [offlineReceiptStatus, setOfflineReceiptStatus] =
    useState<OfflineReceiptStatus>('idle');
  const [offlineReceiptMessage, setOfflineReceiptMessage] = useState('');
  const [checkoutMessage, setCheckoutMessage] = useState('');
  const [introCodeOpen, setIntroCodeOpen] = useState(false);
  const [introCodeInput, setIntroCodeInput] = useState('');
  const [appliedIntroCode, setAppliedIntroCode] =
    useState<AppliedIntroCode | null>(null);
  const [introCodeError, setIntroCodeError] = useState('');

  useEffect(() => {
    const checkoutSuccessMode = consumeCheckoutSuccessReload();
    if (checkoutSuccessMode === 'default') {
      setCheckoutSuccessOpen(true);
      setCheckoutMessage('');
    }
  }, []);
  const plan = selectedPlan ? plans[selectedPlan] : null;
  const planPrice = getPlanPrice(plans, selectedPlan, period);
  const isFreePlanOrder = isFreePlan(selectedPlan) && Boolean(planPrice);
  const selectedFrequency = trackerFrequencies.find(
    (item) => item.id === frequency,
  )!;
  const selectedDepth = trackerResultDepths.find(
    (item) => item.depth === resultDepth,
  );
  const depthMultiplier = selectedDepth?.multiplier ?? 1;
  const trackerPrice = trackerEnabled
    ? isFreePlan(selectedPlan)
      ? 0
      : keywords *
        trackerDailyPrice *
        selectedFrequency.billableDays *
        depthMultiplier
    : 0;
  const selectedMentoringPlan = mentoringPlans.find(
    (item) => item.key === mentoringPlanKey,
  )!;
  const mentoringPrice = mentoringEnabled ? selectedMentoringPlan.price : 0;
  const walletTopUp = !isMemberStep && walletTopUpEnabled ? walletTopUpAmount : 0;
  const planPriceValue = planPrice?.price ?? 0;
  const serviceAddonPrices = [
    ...(trackerPrice > 0 ? [trackerPrice] : []),
    ...(mentoringPrice > 0 ? [mentoringPrice] : []),
  ];
  const serviceSubtotal = planPriceValue + trackerPrice + mentoringPrice;
  const hasPreviousPlanBalance =
    Number.isFinite(previousPlanBalance) && previousPlanBalance > 0;
  const previousPlanDeduction =
    hasPreviousPlanBalance ? Math.min(previousPlanBalance, serviceSubtotal) : 0;

  // Previous-plan credit applies to plan first, then service addons (matches go_handler).
  let planLineBase = planPriceValue;
  let serviceAddonBases = [...serviceAddonPrices];
  let creditLeft = previousPlanDeduction;
  if (creditLeft > planLineBase) {
    creditLeft -= planLineBase;
    planLineBase = 0;
    serviceAddonBases = serviceAddonBases.map((base) => {
      if (creditLeft <= 0) return base;
      if (base <= creditLeft) {
        creditLeft -= base;
        return 0;
      }
      const next = base - creditLeft;
      creditLeft = 0;
      return next;
    });
  } else {
    planLineBase -= creditLeft;
  }

  const subtotalAfterPreviousPlan = Math.max(
    planLineBase + serviceAddonBases.reduce((sum, n) => sum + n, 0),
    0,
  );

  // بدون طرح/خدمات: کد معرف به‌صورت اعتبار کیف پول نمایش داده می‌شود و روی مبلغ اثر ندارد
  const isIntroCodeAsWalletCredit =
    !isMemberStep && walletTopUp > 0 && serviceSubtotal === 0;
  const introCodeOrderAmount = isIntroCodeAsWalletCredit
    ? walletTopUp
    : subtotalAfterPreviousPlan;

  const introBreakdown =
    appliedIntroCode && !isIntroCodeAsWalletCredit
      ? getIntroCodeDiscountBreakdown(
          appliedIntroCode.source,
          planLineBase,
          serviceAddonBases,
        )
      : null;
  const introCodeCredit = appliedIntroCode
    ? isIntroCodeAsWalletCredit
      ? getIntroCodeDiscountAmount(appliedIntroCode.source, introCodeOrderAmount)
      : (introBreakdown?.total ?? 0)
    : 0;
  const introCodeDeduction =
    !isIntroCodeAsWalletCredit && introCodeCredit > 0 ? introCodeCredit : 0;
  const introCodeWalletCredit =
    isIntroCodeAsWalletCredit && introCodeCredit > 0 ? introCodeCredit : 0;

  const planNet = Math.max(
    planLineBase - (introBreakdown?.planDiscount ?? 0),
    0,
  );
  const addonNets = serviceAddonBases.map(
    (base, index) =>
      Math.max(base - (introBreakdown?.addonDiscounts[index] ?? 0), 0),
  );
  const planTax = calcLineTax(planNet);
  const addonTaxes = addonNets.map((net) => calcLineTax(net));
  const walletTax = calcLineTax(walletTopUp);
  const tax =
    planTax + addonTaxes.reduce((sum, n) => sum + n, 0) + walletTax;
  const taxableBase =
    planNet + addonNets.reduce((sum, n) => sum + n, 0) + walletTopUp;
  const orderTotalWithTax = taxableBase + tax;
  const hasWalletBalance = isUsableWalletBalance(walletBalance);
  // موجودی کیف پول شخصی برای کل مبلغ سفارش (خدمات + شارژ افزونه AI + مالیات) قابل کسر است
  const maxWalletDeduction = Math.max(orderTotalWithTax, 0);
  const walletDeduction =
    !isMemberStep && useWallet && hasWalletBalance
      ? Math.min(walletBalance, maxWalletDeduction)
      : 0;
  const total = Math.max(orderTotalWithTax - walletDeduction, 0);
  const walletRemaining = walletBalance - walletDeduction;
  const walletNeeded = maxWalletDeduction > 0;
  const hasOrderItem = Boolean(
    plan ||
      trackerEnabled ||
      mentoringEnabled ||
      (!isMemberStep && walletTopUpEnabled),
  );
  const showWalletOption =
    !isMemberStep &&
    hasWalletBalance &&
    hasOrderItem &&
    maxWalletDeduction > 0;

  let addonDiscountIndex = 0;
  // Invoice shows list price (before period discount); discount column = period + intro code.
  const planListPrice = planPrice?.originalPrice ?? planPrice?.price ?? 0;
  const planPeriodDiscount =
    planPrice?.originalPrice != null
      ? Math.max(planPrice.originalPrice - planPrice.price, 0)
      : 0;
  const planInvoiceDiscount =
    planPeriodDiscount + (introBreakdown?.planDiscount ?? 0);
  const trackerInvoiceDiscount =
    trackerEnabled && trackerPrice > 0
      ? (introBreakdown?.addonDiscounts[addonDiscountIndex++] ?? 0)
      : 0;
  const mentoringInvoiceDiscount =
    mentoringEnabled && mentoringPrice > 0
      ? (introBreakdown?.addonDiscounts[addonDiscountIndex++] ?? 0)
      : 0;

  const invoiceItems: InvoiceData['items'] = [
    ...(plan && planPrice
      ? [
          {
            label: formatPlanInvoiceItemLabel(plan.name, period),
            amount: planListPrice,
            discount: planInvoiceDiscount,
          },
        ]
      : []),
    ...(trackerEnabled
      ? [
          {
            label: fillTemplate(ui.invoice.trackerItemTemplate, {
              keywords: numberFormat.format(keywords),
              frequency: selectedFrequency.label,
              depth: numberFormat.format(resultDepth),
            }),
            amount: trackerPrice,
            discount: trackerInvoiceDiscount,
          },
        ]
      : []),
    ...(mentoringEnabled
      ? [
          {
            label: selectedMentoringPlan.name,
            amount: mentoringPrice,
            discount: mentoringInvoiceDiscount,
          },
        ]
      : []),
    ...(walletTopUp > 0
      ? [
          {
            label: ui.invoice.walletTopUpLabel,
            amount: walletTopUp,
            discount: 0,
          },
        ]
      : []),
  ];
  const invoiceServiceSubtotal =
    (plan && planPrice ? planListPrice : 0) +
    trackerPrice +
    mentoringPrice +
    walletTopUp;
  const invoice: InvoiceData = {
    items: invoiceItems,
    serviceSubtotal: invoiceServiceSubtotal,
    tax,
    previousPlanBalance,
    previousPlanDeduction,
    introCodeDeduction,
    introCodeWalletCredit,
    introCodeDeductionLabel: appliedIntroCode
      ? isIntroCodeAsWalletCredit
        ? getIntroCodeWalletCreditLabel(appliedIntroCode.codeType)
        : getIntroCodeDeductionLabel(appliedIntroCode.codeType)
      : null,
    walletDeduction,
    total,
  };
  const canUseOfflinePayment = hasOrderItem && total > 0;
  const isOfflinePaymentAvailable =
    canUseOfflinePayment && !settings.offlinePaymentDisabled;
  const hasUploadedOfflineReceipt = offlineReceiptStatus === 'uploaded';
  const hasSubmittedOfflineReceipt = offlineReceiptStatus === 'submitted';
  const isOfflineCheckout = offlinePaymentEnabled && isOfflinePaymentAvailable;
  const isOnlineCheckoutBusy = isCreatingOrder || isRedirecting;
  const showInvoicePersonNote =
    hasOrderItem && Boolean(realAndLegal?.name?.trim());
  const showCheckoutTermsAlert =
    checkoutMessage === ui.messages.checkoutRequiresTerms;
  const showInvoiceTermsAlert =
    checkoutMessage === ui.messages.invoiceRequiresTerms;
  const otherCheckoutMessage =
    checkoutMessage && !showCheckoutTermsAlert && !showInvoiceTermsAlert
      ? checkoutMessage
      : '';

  useEffect(() => {
    if (!showWalletOption && useWallet) {
      setUseWallet(false);
    }
  }, [showWalletOption, useWallet]);

  useEffect(() => {
    if (isLoadingTariffs) return;

    let cancelled = false;

    async function tryApplyCookieAffiliateCode() {
      const affiliateCode = getAffiliateCookieCode();
      if (!affiliateCode) return;

      if (!hasOrderItem || introCodeOrderAmount <= 0) return;

      const planId =
        planPrice?.id ?? getNoPlanTariffPriceId(tariffs, period) ?? null;

      if (!planId) {
        setAppliedIntroCode(null);
        setIntroCodeError('');
        return;
      }

      try {
        const response = await validateIntroCode({
          code: affiliateCode,
          order_amount: introCodeOrderAmount,
          plan_id: planId,
        }).unwrap();

        if (cancelled) return;

        if (isApplicableAffiliateIntroCodeResponse(response)) {
          const discountAmount = isIntroCodeAsWalletCredit
            ? getIntroCodeDiscountAmount(response.data, introCodeOrderAmount)
            : getIntroCodeDiscountBreakdown(
                response.data,
                planLineBase,
                serviceAddonBases,
              ).total;

          if (discountAmount <= 0) return;

          setAppliedIntroCode({
            code: response.data.code,
            codeType: response.data.code_type,
            source: response.data,
          });
          setIntroCodeError('');
          return;
        }
      } catch {
        // Cookie-based affiliate validation is best-effort and silent.
      }

      if (!cancelled) {
        setAppliedIntroCode(null);
        setIntroCodeError('');
      }
    }

    void tryApplyCookieAffiliateCode();

    return () => {
      cancelled = true;
    };
  }, [
    isMemberStep,
    isLoadingTariffs,
    hasOrderItem,
    selectedPlan,
    period,
    trackerEnabled,
    keywords,
    frequency,
    resultDepth,
    mentoringEnabled,
    mentoringPlanKey,
    tariffs,
    planPrice,
    introCodeOrderAmount,
    validateIntroCode,
  ]);

  function clearIntroCode() {
    setAppliedIntroCode(null);
    setIntroCodeInput('');
    setIntroCodeError('');
  }

  async function handleApplyIntroCode() {
    const code = introCodeInput.trim();

    if (!code) {
      setIntroCodeError(ui.summary.introCodeRequired);
      return;
    }

    if (code.length > 20) {
      setIntroCodeError(ui.summary.introCodeTooLong);
      return;
    }

    const planId =
      planPrice?.id ?? getNoPlanTariffPriceId(tariffs, period) ?? null;

    if (!planId) {
      setIntroCodeError(ui.messages.missingTariffPrice);
      return;
    }

    if (introCodeOrderAmount <= 0) {
      setIntroCodeError(ui.summary.introCodeInvalid);
      return;
    }

    setIntroCodeError('');

    setIsApplyingManualIntroCode(true);

    try {
      const response = await validateIntroCode({
        code,
        order_amount: introCodeOrderAmount,
        plan_id: planId,
      }).unwrap();

      if (!isValidIntroCodeResponse(response)) {
        setIntroCodeError(
          getIntroCodeErrorMessage(response, ui.summary.introCodeInvalid),
        );
        setAppliedIntroCode(null);
        return;
      }

      setAppliedIntroCode({
        code: response.data.code,
        codeType: response.data.code_type,
        source: response.data,
      });
      setIntroCodeError('');
    } catch (error) {
      setIntroCodeError(
        getIntroCodeErrorFromCatch(error, ui.summary.introCodeRequestFailed),
      );
      setAppliedIntroCode(null);
    } finally {
      setIsApplyingManualIntroCode(false);
    }
  }

  function toggleOfflinePayment() {
    setOfflinePaymentEnabled((current) => {
      const next = !current;

      if (!next) {
        setOfflineReceiptFile(null);
        setOfflineReceiptStatus('idle');
        setOfflineReceiptMessage('');
      }

      setCheckoutMessage('');
      return next;
    });
  }

  async function handleOfflineReceiptChange(
    event: React.ChangeEvent<HTMLInputElement>,
  ) {
    const [file] = event.target.files ?? [];
    if (!file) return;

    if (!termsAccepted) {
      setOfflineReceiptStatus('error');
      setOfflineReceiptFile(null);
      setOfflineReceiptMessage(ui.messages.uploadRequiresTerms);
      event.target.value = '';
      return;
    }

    const maxReceiptSize = 8 * 1024 * 1024;
    if (file.size > maxReceiptSize) {
      setOfflineReceiptStatus('error');
      setOfflineReceiptFile(null);
      setOfflineReceiptMessage(ui.messages.receiptSizeError);
      event.target.value = '';
      return;
    }

    setOfflineReceiptFile(file);
    setOfflineReceiptStatus('uploading');
    setOfflineReceiptMessage(ui.messages.receiptUploading);

    try {
      await uploadOfflineReceipt(file);
      setOfflineReceiptStatus('uploaded');
      setOfflineReceiptMessage(ui.messages.receiptUploaded);
    } catch (error) {
      setOfflineReceiptStatus('error');
      setOfflineReceiptMessage(
        error instanceof Error
          ? error.message
          : ui.messages.receiptUploadFailed,
      );
    } finally {
      event.target.value = '';
    }
  }

  async function handleCheckoutClick() {
    if (isOnlineCheckoutBusy) return;

    if (!hasOrderItem) {
      setCheckoutMessage(ui.messages.selectItemForCheckout);
      return;
    }

    if (!termsAccepted) {
      setCheckoutMessage(ui.messages.checkoutRequiresTerms);
      return;
    }

    if (offlinePaymentEnabled && !hasUploadedOfflineReceipt && !hasSubmittedOfflineReceipt) {
      setCheckoutMessage(ui.messages.offlineRequiresReceipt);
      return;
    }

    if (hasUploadedOfflineReceipt) {
      setOfflineReceiptStatus('submitted');
      setOfflineReceiptMessage(ui.messages.receiptSubmitted);
      setCheckoutMessage('');
      return;
    }

    if (!isOfflineCheckout) {
      const payload = buildCheckoutPayload({
        selectedPlan,
        period,
        plans,
        tariffs,
        trackerEnabled,
        trackerAddonId,
        keywords,
        frequency,
        resultDepth,
        mentoringEnabled,
        selectedMentoringPlan: mentoringEnabled ? selectedMentoringPlan : undefined,
        walletTopUpEnabled: !isMemberStep && walletTopUpEnabled,
        walletTopUpAmount,
        walletAddonId,
      });
      const orderRequest = toCreateOrderRequest(
        payload,
        isMemberStep ? false : useWallet && showWalletOption,
        appliedIntroCode?.code ?? null,
        !isMemberStep,
      );

      if (!orderRequest) {
        setCheckoutMessage(ui.messages.missingTariffPrice);
        return;
      }

      try {
        const response = await createOrder(orderRequest).unwrap();

        if (!response.success || !response.data) {
          const message = response.msg || ui.messages.createOrderFailed;
          setCheckoutErrorVariant(
            response.code === ANNUAL_PLAN_BLOCKS_MONTHLY_CODE ? 'warning' : 'danger',
          );
          setCheckoutErrorMessage(message);
          return;
        }

        if (response.data.need_to_pay && response.data.redirect_url) {
          setIsRedirecting(true);
          window.location.assign(response.data.redirect_url);
          return;
        }

        if (!response.data.need_to_pay) {
          dispatch(walletApi.util.invalidateTags(['Wallet']));

          if (isMemberStep) {
            navigate(ROUTES.MEMBERSHIP_DOMAINS);
            return;
          }

          reloadAfterCheckoutSuccess();
          return;
        }

        throw new Error(ui.messages.createOrderFailed);
      } catch (error) {
        setCheckoutErrorVariant('danger');
        setCheckoutErrorMessage(
          getCreateOrderErrorMessage(error, ui.messages.createOrderFailed),
        );
        return;
      }
    }

    setCheckoutMessage('');
  }

  function handleInvoiceClick() {
    if (!hasOrderItem) {
      setCheckoutMessage(ui.messages.selectItemForInvoice);
      return;
    }

    if (total <= 0) {
      setCheckoutMessage(ui.messages.zeroInvoice);
      return;
    }

    if (!termsAccepted) {
      setCheckoutMessage(ui.messages.invoiceRequiresTerms);
      return;
    }

    setCheckoutMessage('');

    if (!realAndLegal) {
      setLegalModalOpen(true);
      return;
    }

    setInvoiceOpen(true);
  }

  return (
    <aside className="summary-card rounded-4 bg-white dark__bg-card-dark">
      <div className="summary-badge fs--2 rounded-2">
        <Sparkles size={16} />
        {ui.summary.badge}
      </div>

      {plan ? (
        <div className="summary-plan">
          <img className="rounded-3" src={plan.logo} alt="" />
          <div>
            <strong className="text-1000">{plan.name}</strong>
            <span className="text-secondary fs--2">
              {formatPlanSubscriptionLabel(period)}
            </span>
          </div>
        </div>
      ) : (
        <div className="summary-plan no-main-plan">
          <span className="summary-empty-icon rounded-2">
            <PackageOpen size={24} />
          </span>
          <div>
            <strong className="text-1000">{ui.summary.noMainPlanTitle}</strong>
            <span className="text-secondary fs--2">{ui.summary.noMainPlanDescription}</span>
          </div>
        </div>
      )}

      {plan ? (
        <ul className="feature-list">
          {isPaidPlan(selectedPlan)
            ? sortedProjectTools.map((tool) => {
                const included = isProjectToolAccessible(
                  tool.slug,
                  selectedPlan,
                  trackerEnabled,
                );
                const tooltipHtml =
                  tooltipBySlug[tool.slug] ||
                  ('tooltip' in tool && typeof tool.tooltip === 'string'
                    ? tool.tooltip.trim()
                    : '');

                return (
                  <li
                    key={tool.slug}
                    className={`fs--2 text-700 ${included ? 'feature-included' : 'feature-excluded'}`}
                  >
                    {included ? (
                      <CheckCircle2 className="text-success flex-shrink-0" size={14} />
                    ) : (
                      <XCircle2 className="text-danger flex-shrink-0" size={14} />
                    )}
                    {fillTemplate(ui.summary.toolAccessTemplate, {
                      toolName: tool.name,
                    })}
                    {tooltipHtml ? (
                      <HelpTooltip html={tooltipHtml} placement="top" />
                    ) : null}
                  </li>
                );
              })
            : plan.features.map((feature) => (
                <li key={feature} className="fs--2">
                  <CheckCircle2 size={18} />
                  {feature}
                </li>
              ))}
        </ul>
      ) : null}

      <div className="price-details text-700 fs--2">
        {plan && planPrice ? (
          <div>
            <span>
              {fillTemplate(ui.summary.planCostTemplate, {
                planName: plan.name,
              })}
            </span>
            <strong>{planPrice.finalLabel}</strong>
          </div>
        ) : null}
        {trackerEnabled ? (
          <div>
            <span>
              {fillTemplate(ui.summary.trackerLineTemplate, {
                keywords: numberFormat.format(keywords),
                frequency: selectedFrequency.label,
                depth: numberFormat.format(resultDepth),
              })}
            </span>
            <strong>
              {isFreePlan(selectedPlan) ? ui.common.free : formatToman(trackerPrice)}
            </strong>
          </div>
        ) : null}
        {mentoringEnabled ? (
          <div>
            <span>{selectedMentoringPlan.name}</span>
            <strong>{formatToman(mentoringPrice)}</strong>
          </div>
        ) : null}
        {walletTopUpEnabled && !isMemberStep ? (
          <div>
            <span>{ui.summary.walletOnlyLabel}</span>
            <strong>{formatToman(walletTopUp)}</strong>
          </div>
        ) : null}
        {hasPreviousPlanBalance ? (
          <div className="wallet-deduction previous-plan-deduction">
            <span>{ui.summary.previousPlanDeductionLabel}</span>
            <strong className="text-danger">
              {numberFormat.format(previousPlanBalance)} - تومان
            </strong>
          </div>
        ) : null}
        {introCodeDeduction > 0 && appliedIntroCode ? (
          <div className="wallet-deduction intro-code-deduction">
            <span>
              {getIntroCodeDeductionLabel(appliedIntroCode.codeType)}
              {appliedIntroCode.code ? ` (${appliedIntroCode.code})` : ''}
            </span>
            <strong className="text-danger">
              {numberFormat.format(introCodeDeduction)} - تومان
            </strong>
          </div>
        ) : null}
        {introCodeWalletCredit > 0 && appliedIntroCode ? (
          <div className="wallet-deduction intro-code-wallet-credit">
            <span>
              {getIntroCodeWalletCreditLabel(appliedIntroCode.codeType)}
              {appliedIntroCode.code ? ` (${appliedIntroCode.code})` : ''}
            </span>
            <strong className="text-success">
              {numberFormat.format(introCodeWalletCredit)} + تومان
            </strong>
          </div>
        ) : null}
        {hasOrderItem ? (
          <div className="tax-row">
            <span>{ui.summary.taxLabel}</span>
            <strong>{formatToman(tax)}</strong>
          </div>
        ) : (
          <div className="empty-order-note">
            <Info size={16} />
            {ui.summary.emptyOrderNote}
          </div>
        )}
        {walletDeduction > 0 && !isMemberStep ? (
          <div className="wallet-deduction">
            <span>{ui.summary.walletDeductionLabel}</span>
            <strong className="text-danger">
              {numberFormat.format(walletDeduction)} - تومان
            </strong>
          </div>
        ) : null}
      </div>

      {introCodeWalletCredit > 0 ? (
        <Alert variant="info" className="mb-0 mt-3 px-2 py-2 fs--2" role="status">
          {ui.summary.introCodeWalletCreditAlert}
        </Alert>
      ) : null}

      {hasOrderItem ? (
        <div className="mt-3">
          {appliedIntroCode ? (
            <div className="d-flex align-items-center justify-content-between gap-2 border rounded-3 p-2 bg-body-tertiary">
              <div className="min-w-0 d-flex flex-column gap-1">
                <strong className="text-1000 fs--2">
                  {ui.summary.introCodeApplied}
                </strong>
                <small className="text-secondary text-truncate">
                  {appliedIntroCode.code}
                </small>
              </div>
              <Button
                type="button"
                variant="link"
                size="sm"
                className="flex-shrink-0 p-0 text-danger fs--2 text-decoration-none"
                onClick={clearIntroCode}
              >
                {ui.summary.introCodeClear}
              </Button>
            </div>
          ) : (
            <>
              <Button
                type="button"
                variant="link"
                className="p-0 fs--2 fw-semi-bold text-decoration-none"
                aria-expanded={introCodeOpen}
                onClick={() => {
                  setIntroCodeOpen((current) => !current);
                  setIntroCodeError('');
                }}
              >
                {ui.summary.introCodePrompt}
              </Button>
              {introCodeOpen ? (
                <Form
                  className="d-flex align-items-stretch gap-3 mt-3"
                  onSubmit={(event) => {
                    event.preventDefault();
                    void handleApplyIntroCode();
                  }}
                >
                  <Form.Control
                    type="text"
                    className="fs--2"
                    value={introCodeInput}
                    maxLength={20}
                    placeholder={ui.summary.introCodePlaceholder}
                    autoFocus
                    disabled={isApplyingManualIntroCode}
                    isInvalid={Boolean(introCodeError)}
                    onChange={(event) => {
                      setIntroCodeInput(event.target.value);
                      if (introCodeError) setIntroCodeError('');
                    }}
                  />
                  <Button
                    type="submit"
                    variant="falcon-primary"
                    className="flex-shrink-0 fs--2"
                    disabled={isApplyingManualIntroCode}
                  >
                    {isApplyingManualIntroCode ? (
                      <>
                        <Spinner
                          animation="border"
                          size="sm"
                          className="me-1"
                          style={{ width: 14, height: 14 }}
                        />
                        {ui.summary.introCodeApplying}
                      </>
                    ) : (
                      ui.summary.introCodeApply
                    )}
                  </Button>
                </Form>
              ) : null}
              {introCodeError ? (
                <Alert
                  variant="danger"
                  className="mb-0 mt-2 px-2 py-1 fs--2"
                  role="alert"
                >
                  {introCodeError}
                </Alert>
              ) : null}
            </>
          )}
        </div>
      ) : null}

      {showWalletOption ? (
        <div className="wallet-option-block">
          <button
            type="button"
            className={`wallet-option rounded-3 ${useWallet ? 'active' : ''}`}
            role="checkbox"
            aria-checked={useWallet}
            disabled={!walletNeeded}
            onClick={() => setUseWallet((current) => !current)}
          >
            <span className="wallet-option-icon rounded-3">
              <Wallet size={20} />
            </span>
            <span className="wallet-option-copy">
              <strong className="text-1000 fs--2">{ui.summary.walletOptionTitle}</strong>
              <small>
                {!walletNeeded
                  ? ui.summary.walletSettledByPreviousPlan
                  : useWallet
                    ? `${ui.summary.walletRemainingPrefix} ${formatToman(walletRemaining)}`
                    : `${ui.summary.walletBalancePrefix} ${formatToman(walletBalance)}`}
              </small>
            </span>
            <span className="wallet-checkbox rounded-2" aria-hidden="true">
              <Check size={14} strokeWidth={3} />
            </span>
          </button>
        </div>
      ) : null}

      <div className="total-row">
        <div>
          <span>{ui.summary.totalLabel}</span>
          <small>
            {plan
              ? formatPlanPeriodSummaryLabel(period)
              : !isMemberStep && walletTopUpEnabled && !trackerEnabled && !mentoringEnabled
                ? ui.summary.walletOnlyLabel
                : ui.summary.addonsMonthlyLabel}
          </small>
        </div>
        <strong className="text-1000">{formatToman(total)}</strong>
      </div>

      {planPrice?.originalLabel ? (
        <div className="saving-line text-secondary fs--2">
          <span>{ui.summary.previousPriceLabel}</span>
          <del>{planPrice.originalLabel}</del>
        </div>
      ) : null}

      {showInvoicePersonNote ? (
        <div className="invoice-person-note fs--2 rounded-3 text-secondary">
          <Info size={15} />
          <span>
            {ui.summary.invoicePersonNotePrefix}{' '}
            <strong className="text-1000">{realAndLegal?.name.trim()}</strong>{' '}
            {ui.summary.invoicePersonNoteSuffix}
          </span>
        </div>
      ) : null}

      {hasOrderItem ? (
        <div className={`terms-option text-secondary ${termsAccepted ? 'accepted' : ''}`}>
          <button
            type="button"
            className="terms-check-control"
            role="checkbox"
            aria-checked={termsAccepted}
            aria-label="پذیرش شرایط استفاده از خدمات"
            onClick={() => {
              setTermsAccepted((current) => {
                const next = !current;
                if (next) {
                  setCheckoutMessage((message) =>
                    message === ui.messages.checkoutRequiresTerms ||
                    message === ui.messages.invoiceRequiresTerms
                      ? ''
                      : message,
                  );
                }
                return next;
              });
            }}
          >
            <span className="terms-checkbox rounded-1" aria-hidden="true">
              <Check size={13} strokeWidth={3} />
            </span>
          </button>
          <span className="fs--2">
            {ui.summary.termsTextBeforeLink}{' '}
            <a
              href={settings.termsUrl}
              target="_blank"
              rel="noreferrer"
            >
              {ui.summary.termsLinkText}
            </a>{' '}
            {ui.summary.termsTextAfterLink}
          </span>
        </div>
      ) : null}

      {canUseOfflinePayment ? (
        settings.offlinePaymentDisabled ? (
          <div className="offline-toggle-wrap offline-toggle-wrap--disabled">
            <div
              className="offline-toggle-disabled-tooltip rounded-3 fs--2"
              role="tooltip"
            >
              {ui.summary.offlineComingSoon}
            </div>
            <button
              type="button"
              className="offline-toggle rounded-3"
              role="checkbox"
              aria-checked={false}
              aria-disabled="true"
              disabled
            >
              <span className="offline-toggle-checkbox rounded-1" aria-hidden="true">
                <Check size={13} strokeWidth={3} />
              </span>
              <span className="fs--2">
                {ui.summary.offlineToggleTitle}
                <small>{ui.summary.offlineToggleDescription}</small>
              </span>
            </button>
          </div>
        ) : (
          <button
            type="button"
            className={`offline-toggle rounded-3 ${offlinePaymentEnabled ? 'active' : ''}`}
            role="checkbox"
            aria-checked={offlinePaymentEnabled}
            onClick={toggleOfflinePayment}
          >
            <span className="offline-toggle-checkbox rounded-1" aria-hidden="true">
              <Check size={13} strokeWidth={3} />
            </span>
            <span className="fs--2">
              {ui.summary.offlineToggleTitle}
              <small>{ui.summary.offlineToggleDescription}</small>
            </span>
          </button>
        )
      ) : null}

      {isOfflinePaymentAvailable && offlinePaymentEnabled ? (
        <section className={`offline-payment-card rounded-3 ${offlineReceiptStatus}`}>
          <div className="offline-payment-copy">
            <span className="offline-payment-icon rounded-3">
              <UploadCloud size={18} />
            </span>
            <div>
              <strong className="text-1000 fs--2">{ui.summary.offlineCardTitle}</strong>
              <small>{ui.summary.offlineCardDescription}</small>
            </div>
          </div>
          <label className="offline-upload-button fs--2 rounded-3">
            <input
              type="file"
              accept="image/*,.pdf"
              disabled={offlineReceiptStatus === 'uploading'}
              onChange={handleOfflineReceiptChange}
            />
            <UploadCloud size={16} />
            {offlineReceiptStatus === 'uploading'
              ? ui.summary.offlineUploadLoading
              : offlineReceiptFile
                ? ui.summary.offlineUploadChange
                : ui.summary.offlineUploadIdle}
          </label>
          <p className="offline-review-note">
            {ui.summary.offlineReviewNote}
          </p>
          {offlineReceiptMessage ? (
            <p className={`offline-status-message ${offlineReceiptStatus}`}>
              {offlineReceiptMessage}
            </p>
          ) : null}
        </section>
      ) : null}

      <div className="checkout-actions">
        {showInvoiceTermsAlert ? (
          <Alert
            variant="danger"
            className="mb-0 px-1 py-2 fs--2 d-flex align-items-start gap-1"
            role="alert"
          >
            <Info size={15} aria-hidden="true" className="flex-shrink-0" />
            <span>{ui.messages.invoiceRequiresTerms}</span>
          </Alert>
        ) : null}
        <button
          type="button"
          className="invoice-button rounded-3 text-secondary"
          onClick={handleInvoiceClick}
          hidden={isFreePlanOrder}
        >
          <FileText size={18} />
          {ui.summary.invoiceButton}
        </button>
        {showCheckoutTermsAlert ? (
          <Alert
            variant="danger"
            className="mb-0 px-1 py-2 fs--2 d-flex align-items-start gap-1"
            role="alert"
          >
            <Info size={15} aria-hidden="true" className="flex-shrink-0" />
            <span>{ui.messages.checkoutRequiresTerms}</span>
          </Alert>
        ) : null}
        <button
          type="button"
          className="checkout-button rounded-3"
          disabled={isOnlineCheckoutBusy}
          onClick={handleCheckoutClick}
        >
          {isOnlineCheckoutBusy ? (
            <>
              <Spinner
                animation="border"
                size="sm"
                className="me-2"
                role="status"
                aria-hidden="true"
              />
              {ui.summary.onlineCheckoutLoading}
            </>
          ) : (
            <>
              {isOfflineCheckout
                ? ui.summary.offlineCheckoutButton
                : total <= 0 && hasOrderItem
                  ? ui.summary.zeroCheckoutButton
                  : isFreePlanOrder
                    ? ui.summary.freeCheckoutButton
                    : ui.summary.onlineCheckoutButton}
              <ArrowLeft size={20} />
            </>
          )}
        </button>
      </div>
      {otherCheckoutMessage ? (
        <p className="checkout-feedback rounded-3" role="alert">
          {otherCheckoutMessage}
        </p>
      ) : null}
      {invoiceOpen ? (
        <InvoiceModal invoice={invoice} onClose={() => setInvoiceOpen(false)} />
      ) : null}
      <LegalModal
        show={legalModalOpen}
        setShow={setLegalModalOpen}
        onSuccess={() => {
          setLegalModalOpen(false);
          setLegalSuccessOpen(true);
          dispatch(pricingApi.util.invalidateTags(['Pricing']));
        }}
      />
      <CheckoutFeedbackModal
        show={legalSuccessOpen}
        variant="success"
        title={ui.messages.legalInfoSuccessModalTitle}
        message={ui.messages.legalInfoSuccessModalMessage}
        showHomeButton={false}
        onClose={() => setLegalSuccessOpen(false)}
      />
      <CheckoutFeedbackModal
        show={Boolean(checkoutErrorMessage)}
        variant={checkoutErrorVariant}
        title={ui.messages.checkoutErrorModalTitle}
        message={checkoutErrorMessage ?? ''}
        onClose={() => setCheckoutErrorMessage(null)}
      />
      <CheckoutFeedbackModal
        show={checkoutSuccessOpen}
        variant="success"
        title={ui.messages.checkoutSuccessModalTitle}
        message={ui.messages.createOrderSuccess}
        successRedirectTo={isMemberStep ? '/membership-steps/domains' : '/'}
        successButtonLabel={
          isMemberStep
            ? ui.messages.checkoutSuccessGoDomains
            : ui.messages.checkoutSuccessGoHome
        }
        onClose={() => setCheckoutSuccessOpen(false)}
      />
    </aside>
  );
}

export default Summary;
